/**
 * Shared two-layer compound-tool dispatch scaffolding (WP-484 S1, §3/§4).
 *
 * ── The mechanism (empirically proven, S1 SDK spike) ─────────────────────
 *
 * Layer (a) — REGISTERED: every compound tool registers a FLAT ZodObject
 * (`{ action: z.enum([...]), ...allParams }`) as its `inputSchema`. This is
 * mandatory, never a top-level `z.discriminatedUnion` — on SDK 1.27.1 (the
 * pinned dependency) `tools/list` serialization
 * (`normalizeObjectSchema` in `@modelcontextprotocol/sdk/dist/cjs/server/zod-compat.js`)
 * only recognizes schemas exposing `.shape` (zod v3) or `def.type === 'object'`
 * (zod v4) — a union has neither, so it silently falls back to
 * `EMPTY_OBJECT_JSON_SCHEMA` and the client sees a parameterless tool while
 * call-time validation keeps working (a silent, tests-green failure).
 *
 * **S1 SPIKE (§3c) — re-verified against the newest published SDK (1.29.0,
 * fetched via `npm pack` for inspection, not installed): the SAME
 * `normalizeObjectSchema` gate ships unchanged in 1.29.0 — it still only
 * recognizes `.shape` / `def.type === 'object'`, so a `discriminatedUnion`
 * still erases to `EMPTY_OBJECT_JSON_SCHEMA` there too.** Outcome: DO NOT
 * upgrade — the two-layer shape is not a workaround for a fixed-in-newer-SDK
 * bug, it is the correct shape for every SDK version available today. This
 * spike is re-run (and this comment re-verified) before any future SDK bump.
 *
 * Layer (b) — HANDLER: an internal per-action `z.discriminatedUnion("action", [...])`
 * validates the call once past the registered layer. Call-time validation of
 * unions is the half that's proven to work.
 *
 * ── Division of labor (§4, Pass-1 Domain D4, amended v3.1) ────────────────
 *
 * The SDK's registered-layer validation PREEMPTS the handler for two of three
 * wrong-call classes — (i) an unknown `action` and (ii) a wrong param TYPE —
 * both return the registered layer's raw zod error in-band before the handler
 * ever runs. That is accepted, not fought: zod's enum error already carries
 * the valid-options list, so agents self-correct on it (`action` is
 * deliberately NOT loosened to `z.string()` — the served enum's
 * discoverability is the point). Everything the deliberately-loose flat layer
 * passes through — a valid action with a wrong param COMBINATION, or a
 * per-action-enum violation — reaches `dispatchDiscriminated` below, which
 * answers with the structured backpressure `backpressureResult()` builds:
 * never raw zod text on this path.
 */

import { z, type ZodDiscriminatedUnion } from "zod/v3";
import { textContent, validationError, type ToolResult } from "./envelope.js";

/**
 * One entry per action — a human-readable param spec used ONLY to build the
 * backpressure message, never for registration (the registered schema is the
 * flat ZodObject built separately per §3a, in each tool's own schema file).
 */
export interface ActionParamSpec {
  /**
   * Declared param names this action accepts. Absence-only (§4 probe note):
   * the SDK silently strips undeclared/typo'd params before the handler ever
   * sees them, so the tip can only ever say a declared param is "missing" —
   * never suggest a misspelling correction.
   */
  readonly params: readonly string[];
  /** One-line description of what this action does / requires. */
  readonly description: string;
}

/**
 * Structured wrong-call backpressure (§4) — the handler-layer's answer for a
 * call that passed the registered layer's action-enum + type checks but
 * violates the per-action discriminated shape. Never raw zod text.
 */
export function backpressureResult(
  toolName: string,
  action: string,
  spec: ActionParamSpec,
  detail: string,
): ToolResult {
  const paramList = spec.params.length > 0 ? spec.params.join(", ") : "(no params)";
  const tip = `action '${action}' needs: ${paramList}. ${spec.description}`;
  const message = `${toolName} action='${action}': ${detail}`;
  return {
    content: textContent(message),
    structuredContent: {
      ...validationError(message),
      _meta: { tip, advisory: { action, expectedParams: spec.params, reason: detail } },
    },
  };
}

/**
 * Handler-layer dispatch (§3b): validates `flatData` (the already-registered-layer-
 * parsed args) against the internal discriminatedUnion, then routes to the
 * matching handler. A discriminatedUnion failure here is, by construction,
 * ALWAYS the "valid action, wrong param combination" case (the flat layer
 * already rejected unknown actions and wrong param types) — so it always
 * answers via `backpressureResult`, never raw zod text.
 */
export async function dispatchDiscriminated<
  Union extends ZodDiscriminatedUnion<"action", readonly [z.ZodObject<any>, ...z.ZodObject<any>[]]>,
>(
  toolName: string,
  union: Union,
  flatData: Record<string, unknown>,
  actionSpecs: Record<string, ActionParamSpec>,
  handlers: Record<string, (data: any) => Promise<ToolResult>>,
): Promise<ToolResult> {
  const action = String(flatData.action ?? "");
  const parsed = union.safeParse(flatData);
  if (!parsed.success) {
    const spec = actionSpecs[action];
    const detail = parsed.error.issues
      .map((i) => `${i.path.join(".") || action}: ${i.message}`)
      .join("; ");
    if (!spec) {
      // Should be unreachable — the registered flat layer already validated the
      // action enum — but fail closed rather than throw.
      return backpressureResult(toolName, action, { params: [], description: "unknown action" }, detail);
    }
    return backpressureResult(toolName, action, spec, detail);
  }
  const handler = handlers[action];
  if (!handler) {
    const spec = actionSpecs[action] ?? { params: [], description: "unknown action" };
    return backpressureResult(toolName, action, spec, `no handler registered for action '${action}'`);
  }
  return handler(parsed.data);
}

// ─── Batch validate-all-then-apply (§4, PAT-268 item 1) ───────────────────
// Scaffolding for S2's `capture items[]` / `relations batch-create` — not
// consumed by any S1 noun (entries has no batch-WRITE action), built now so
// every noun that migrates a batch-write action reuses one implementation.

export interface BatchApplyResult<T> {
  ok: true;
  results: T[];
}

export interface BatchValidationFailure {
  ok: false;
  errors: { index: number; message: string }[];
}

/**
 * Validates every item against `itemSchema` BEFORE applying any — a batch
 * either fully validates or nothing is written (PAT-268 item 1). `apply` runs
 * only after every item parses; failures short-circuit with per-index detail.
 */
export async function validateAllThenApply<Item, Out>(
  items: readonly unknown[],
  itemSchema: z.ZodType<Item>,
  apply: (validItems: Item[]) => Promise<Out[]>,
): Promise<BatchApplyResult<Out> | BatchValidationFailure> {
  const errors: { index: number; message: string }[] = [];
  const validated: Item[] = [];
  items.forEach((item, index) => {
    const parsed = itemSchema.safeParse(item);
    if (!parsed.success) {
      errors.push({
        index,
        message: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; "),
      });
    } else {
      validated.push(parsed.data as Item);
    }
  });
  if (errors.length > 0) return { ok: false, errors };
  const results = await apply(validated);
  return { ok: true, results };
}
